import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { creditsAPI, sitesAPI } from '@/lib/api';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Coins, Loader2, ArrowLeft, Zap, History } from 'lucide-react';

export default function CreditsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [packages, setPackages] = useState([]);
  const [balances, setBalances] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [sitesRes, balRes, histRes] = await Promise.all([
          sitesAPI.getAll(),
          creditsAPI.getBalance(),
          creditsAPI.getHistory(),
        ]);
        setSites(sitesRes.data);
        setBalances(balRes.data);
        setHistory(histRes.data);

        // Load packages for all sites
        const pkgRes = await creditsAPI.getPackages();
        setPackages(pkgRes.data);
      } catch {}
      setLoading(false);
    };
    load();
  }, []);

  const handlePurchase = async (pkg) => {
    setPurchasing(pkg.id);
    try {
      const res = await creditsAPI.purchase({
        package_id: pkg.id,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'achat');
      setPurchasing(null);
    }
  };

  const getSiteName = (siteId) => sites.find(s => s.id === siteId)?.city || siteId;

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
      <Loader2 className="w-8 h-8 animate-spin text-[#00FF00]" />
    </div>
  );

  // Group packages by site
  const packagesBySite = {};
  packages.forEach(pkg => {
    if (!packagesBySite[pkg.site_id]) packagesBySite[pkg.site_id] = [];
    packagesBySite[pkg.site_id].push(pkg);
  });

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-8" data-testid="credits-page">
      <div className="max-w-5xl mx-auto px-6 lg:px-12">
        <button onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-500 hover:text-[#00FF00] text-sm mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>

        <div className="mb-10">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white flex items-center gap-3" data-testid="credits-title">
            <Coins className="w-8 h-8 text-[#00FF00]" /> Mes Crédits
          </h1>
          <p className="text-slate-500 mt-2">Achetez des crédits et économisez sur vos réservations</p>
        </div>

        {/* Current balances */}
        {balances.length > 0 && (
          <div className="mb-10">
            <h2 className="font-heading text-lg font-bold text-white mb-4">Votre solde</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {balances.map(b => (
                <div key={`${b.user_id}-${b.site_id}`}
                  className="relative overflow-hidden rounded-2xl p-6 bg-gradient-to-br from-[#00FF00]/20 to-[#00FF00]/5 border border-[#00FF00]/30 shadow-[0_0_30px_rgba(0,255,0,0.1)]"
                  data-testid={`balance-card-${b.site_id}`}>
                  <div className="flex justify-between items-start mb-4">
                    <Badge className="bg-[#00FF00]/15 text-[#00FF00] border-[#00FF00]/30 text-xs">
                      {getSiteName(b.site_id)}
                    </Badge>
                    <Coins className="w-5 h-5 text-[#00FF00]/50" />
                  </div>
                  <p className="text-xs text-slate-500">Solde disponible</p>
                  <p className="font-heading text-3xl font-bold text-[#00FF00]" data-testid={`balance-amount-${b.site_id}`}>
                    {b.balance}€
                  </p>
                  <div className="flex justify-between mt-3 text-xs text-slate-500">
                    <span>Acheté: {b.total_purchased || 0}€</span>
                    <span>Utilisé: {b.total_used || 0}€</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Available packages */}
        {Object.keys(packagesBySite).length > 0 ? (
          Object.entries(packagesBySite).map(([siteId, pkgs]) => (
            <div key={siteId} className="mb-10">
              <h2 className="font-heading text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#00FF00]" /> Forfaits — {getSiteName(siteId)}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pkgs.map(pkg => {
                  const bonus = pkg.credits - pkg.price;
                  const bonusPct = ((pkg.credits / pkg.price - 1) * 100).toFixed(0);
                  return (
                    <div key={pkg.id}
                      className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] p-6 hover:border-[#00FF00]/50 transition-all duration-200 flex flex-col"
                      data-testid={`credit-package-${pkg.id}`}>
                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-4">
                          <h3 className="font-heading text-base font-bold text-white">{pkg.name}</h3>
                          {bonus > 0 && (
                            <Badge className="bg-orange-500/15 text-orange-400 border-0 text-xs">
                              +{bonusPct}%
                            </Badge>
                          )}
                        </div>
                        <div className="mb-4">
                          <div className="flex items-baseline gap-2">
                            <span className="font-heading text-3xl font-bold text-[#00FF00]">{pkg.credits}€</span>
                            <span className="text-sm text-slate-500">de crédits</span>
                          </div>
                          <p className="text-sm text-slate-400 mt-1">pour seulement <span className="font-bold text-white">{pkg.price}€</span></p>
                          {bonus > 0 && (
                            <p className="text-xs text-[#00FF00] mt-1">Vous économisez {bonus}€</p>
                          )}
                        </div>
                        {pkg.description && <p className="text-xs text-slate-500 mb-4">{pkg.description}</p>}
                      </div>
                      <Button
                        onClick={() => handlePurchase(pkg)}
                        disabled={purchasing === pkg.id}
                        className="w-full bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold shadow-[0_0_20px_rgba(0,255,0,0.2)]"
                        data-testid={`buy-package-${pkg.id}`}>
                        {purchasing === pkg.id ? (
                          <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Redirection...</>
                        ) : (
                          `Acheter pour ${pkg.price}€`
                        )}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-16 bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A]">
            <Coins className="w-12 h-12 mx-auto text-slate-600 mb-4" />
            <p className="text-slate-500">Aucun forfait disponible pour le moment</p>
          </div>
        )}

        {/* History */}
        {history.length > 0 && (
          <div className="mb-10">
            <button onClick={() => setShowHistory(!showHistory)}
              className="flex items-center gap-2 font-heading text-lg font-bold text-white mb-4 hover:text-[#00FF00] transition-colors">
              <History className="w-5 h-5" /> Historique {showHistory ? '▾' : '▸'}
            </button>
            {showHistory && (
              <div className="space-y-2">
                {history.map(tx => (
                  <div key={tx.id} className="flex justify-between items-center bg-[#1A1A1A] rounded-xl border border-[#2A2A2A] px-4 py-3 text-sm">
                    <div>
                      <p className="text-white font-medium">{tx.description}</p>
                      <p className="text-slate-600 text-xs">{getSiteName(tx.site_id)} • {new Date(tx.created_at).toLocaleString('fr-FR')}</p>
                    </div>
                    <span className={`font-bold ${tx.amount > 0 ? 'text-[#00FF00]' : 'text-red-400'}`}>
                      {tx.amount > 0 ? '+' : ''}{tx.amount}€
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
